import { Form } from 'formik';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IDamageReportReducer } from '../../../../reducers/damageReportReducer';
import { RootState } from '../../../../store';
import Button from '../../../base/button';
import FormikCheckbox from '../../../base/forms/formikCheckbox';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import FormikSelect from '../../../base/forms/formikSelect';
import { StyledFormikField } from '../../../base/forms/input';
import { Column, Columns } from '../../../base/grid';
import { H4 } from '../../../base/typography/headings';
import * as StyledStepPaymentInfoForm from './stepPaymentInfoForm.styles';

interface Props {
	values: any;
	errors: any;
	onPrevious: any;
	setFieldTouched: any;
	setFieldValue: any;
	isSubmitting: any;
	isValid: any;
}

const StepPaymentInfoForm: FC<Props> = ({ values, setFieldValue, setFieldTouched, onPrevious, isSubmitting }) => {
	const { t } = useTranslation(['registerShipment', 'damageReport']);

	const damageReportReducer: IDamageReportReducer = useSelector((store: RootState) => store.damageReportReducer);
	const { stepInfo } = damageReportReducer;

	const options = [
		{
			label: `${t('shared.sender')}`,
			value: 'sender',
		},
		{
			label: `${t('shared.recipient')}`,
			value: 'recipient',
		},
		{
			label: `${t('shared.otherPerson')}`,
			value: 'other',
		},
	];

	const onWhoToPayChange = (key, option) => {
		if (key && option) {
			setFieldValue(key, option);
			if (option.value === 'sender') {
				setFieldValue('ssn', stepInfo.senderSsn);
			} else if (option.value === 'recipient') {
				setFieldValue('ssn', stepInfo.receiverSsn);
			} else {
				setFieldValue('ssn', '');
			}
		}
	};

	return (
		<Form>
			<H4 marginBottom="2rem">{`${t('whoToPay')}?`}</H4>
			<Columns>
				<Column>
					<FormikCheckbox
						name="payerIsForeign"
						id="payerIsForeign"
						label={t('damageReport:payer-has-foreign-bankaccount')}
						setFieldValue={setFieldValue}
					/>
				</Column>
				<Column tablet={1 / 1}>
					<FormikFieldWrapper
						label={`${t('whoToPay')}*`}
						name="whoToPay"
						languageComponent="registerShipment"
						smallLabel>
						<FormikSelect
							id="whoToPay"
							name="whoToPay"
							options={options}
							value={values.whoToPay}
							onBlur={setFieldTouched}
							onChange={(e, v) => onWhoToPayChange(e, v)}
							placeholder={t('shared.choose')}
						/>
					</FormikFieldWrapper>
				</Column>
				{!values.payerIsForeign && (
					<>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								label={`${t('step-optional-services.ssn-recipient')}*`}
								name="ssn"
								languageComponent="registerShipment"
								smallLabel>
								<StyledFormikField
									type="text"
									id="ssn"
									name="ssn"
									placeholder={t('step-optional-services.ssn-recipient')}
								/>
							</FormikFieldWrapper>
						</Column>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								label={`${t('step-optional-services.bank')}*`}
								name="bank"
								languageComponent="registerShipment"
								smallLabel>
								<StyledFormikField
									type="text"
									id="bank"
									name="bank"
									placeholder={t('step-optional-services.bank')}
								/>
							</FormikFieldWrapper>
						</Column>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								label={`${t('step-optional-services.account-type')}*`}
								name="accountType"
								languageComponent="registerShipment"
								smallLabel>
								<StyledFormikField
									type="text"
									id="accountType"
									name="accountType"
									placeholder={t('step-optional-services.account-type')}
								/>
							</FormikFieldWrapper>
						</Column>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								label={`${t('step-optional-services.account')}*`}
								name="account"
								languageComponent="registerShipment"
								smallLabel>
								<StyledFormikField
									type="text"
									id="account"
									name="account"
									placeholder={t('step-optional-services.account')}
								/>
							</FormikFieldWrapper>
						</Column>
					</>
				)}

				{values.payerIsForeign && (
					<>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								label="IBAN*"
								name="iban"
								languageComponent="registerShipment"
								smallLabel>
								<StyledFormikField type="text" id="iban" name="iban" placeholder="IBAN" />
							</FormikFieldWrapper>
						</Column>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								label="SWIFT*"
								name="swift"
								languageComponent="registerShipment"
								smallLabel>
								<StyledFormikField type="text" id="swift" name="swift" placeholder="SWIFT" />
							</FormikFieldWrapper>
						</Column>
					</>
				)}
			</Columns>
			<StyledStepPaymentInfoForm.ButtonWrapper>
				<Button type="button" onClick={() => onPrevious(values)} wide>
					{t('shared.previous-step')}
				</Button>
				<Button wide primary type="submit" isLoading={isSubmitting}>
					{t('damageReport:sendReport')}
				</Button>
			</StyledStepPaymentInfoForm.ButtonWrapper>
		</Form>
	);
};

export default StepPaymentInfoForm;
